import { Component, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { RiskEquationPart, RiskFormula } from '../../../../models/risk-formula';
import { RiskFactor } from '../../../../models/risk-factor';
import { AgilityRiskFormulaHelperService } from '../../../../providers/agility-risk-formula-helper.service';
import { EquationPartTypes } from '../../../../models/risk-equation-part-type';
import { RiskBand } from '../../../../models/risk-band';
import 'chartjs-plugin-annotation';
import { AgilityRiskService } from '../../../../providers/agility-risk.service';
import { AgilityRiskSpinnerService } from '../../../../providers/agility-risk-spinner.service';
import { AgilityRiskMessageService } from '../../../../providers/agility-risk-message.service';

@Component({
  selector: 'lib-test-formula-modal',
  templateUrl: './test-formula-modal.component.html',
  styleUrls: ['./test-formula-modal.component.scss']
})
export class TestFormulaModalComponent implements OnInit {
  public formula: RiskFormula;
  public readonlyFormula: string;
  public riskFactors: RiskFactor[];
  public testResult: number;
  public riskBands: RiskBand[];
  public min = 1; // by default min is always 1
  public annotations = [];

  threatsHorizontalChartData = {
    labels: [''],
    datasets: [
      {
        backgroundColor: '#FFB74D', // TODO: colors....
        data: [],
        barThickness: 20
      }
    ]
  };

  threatsHorizontalChartOptions = {};

  constructor(private dlgRef: DynamicDialogRef,
              private dialogConfig: DynamicDialogConfig,
              private translateService: TranslateService,
              private formulaHelper: AgilityRiskFormulaHelperService,
              private agilityRiskService: AgilityRiskService,
              private messageService: AgilityRiskMessageService,
              private spinner: AgilityRiskSpinnerService) {
  }

  ngOnInit(): void {
    if (this.dialogConfig?.data?.formula) {
      this.formula = this.dialogConfig.data.formula;
      // small optimization - formula array is used twice
      const formulaAsArray = this.formulaHelper.getRiskFormulaAsArray(this.formula);
      this.readonlyFormula = this.formulaHelper.getFormulaAsString(formulaAsArray);
      // filter out risk factors not in formula
      this.riskFactors = this.formulaHelper.getFactorsUsedInFormula(formulaAsArray, this.dialogConfig.data.riskFactors);
      this.riskFactors.forEach(rfact => rfact.numericValue = Math.floor(rfact.ranks.length / 2));
      this.riskBands = this.dialogConfig.data.riskBands;
      this.defineAnnotations();
      this.onTestFormula();
    }
  }

  defineAnnotations(): void {
    this.riskBands.forEach((rb: RiskBand) => {
      this.annotations.push({
        drawTime: 'beforeDatasetsDraw',
        type: 'line',
        mode: 'vertical',
        scaleID: 'x-axis-0',
        value: rb.numericValue,
        borderColor: '#D3D3D3', // todo: what color?
      });
    });
  }

  onTestFormula() {
    this.spinner.start(this.translateService.instant('common.calculating'));
    this.agilityRiskService.testRiskFormula(this.formula, this.riskFactors).subscribe({
      next: res => {
        this.testResult = res.result;
        this.displayResult();
        this.spinner.stop();
      },
      error: err => {
        this.messageService.error(this.translateService.instant('common.error'), this.translateService.instant('formulaManagement.testFormulaModal.invalidFormulaError'));
        this.spinner.stop();
      }
    });
  }

  onClose() {
    this.dlgRef.destroy();
  }

  displayResult() {
    this.threatsHorizontalChartData = {
      labels: [this.translateService.instant('formulaManagement.testFormulaModal.result')],
      datasets: [
        {
          backgroundColor: '#FFB74D', // TODO: colors....
          data: [this.testResult],
          barThickness: 20
        }
      ]
    };

    this.threatsHorizontalChartOptions = {
      responsive: true,
      legend: {
        display: false
      },
      scales: {
        xAxes: [{
          gridLines: {
            display: false
          },
          scaleLabel: {
            display: true,
            fontSize: 14,
            fontStyle: 'bold'
          },
          ticks: {
            min: 0,
            max: this.riskBands[this.riskBands.length - 1]?.numericValue,
            stepSize: 1,
            beginAtZero: true,
            callback: (val, index) => {
              return this.riskBands.find((rb: RiskBand) => rb.numericValue === val)?.name || '';
            }
          }
        }]
      },
      annotation: {
        annotations: this.annotations
      }
    };
  }
}
